import { JSXElementConstructor, ReactNode } from 'react';

import { APP_CONFIG_CANARYAI } from '@hcs/app-configs';
import {
  AdvancedApiIcon,
  Anchor,
  AvmReportsIncludedIcon,
  CoreApiIcon,
  CustomerSupportIcon,
  CustomQuestionnairesIcon,
  IncludedUsersIcon,
  NationwideBrokerageIcon,
  ProductGridIcon,
  PropertiesIcon,
  ScalableDatasetIcon,
  Soc2Icon,
  SolutionsEngineerIcon,
  SsoIcon,
  ValuationReportsIncludedIcon,
} from '@hcs/design-system';
import { IconProps, SelfServePlan, SelfServicePlanTier } from '@hcs/types';
import { formatNumber } from '@hcs/utils';

interface SelfServePlanFeatureConfig {
  key: string;
  Icon: JSXElementConstructor<IconProps>;
  Label:
    | ReactNode
    | JSXElementConstructor<{
        enterprise?: boolean;
        plan?: SelfServePlan;
        onClick?: VoidFunction;
      }>;
}
export const SELF_SERVE_PLAN_FEATURES: Record<
  string,
  SelfServePlanFeatureConfig
> = {
  'included-users': {
    key: 'included-users',
    Icon: IncludedUsersIcon,
    Label: ({ plan, enterprise }) => {
      if (enterprise) {
        return (
          <>
            <b>Custom</b> user limits
          </>
        );
      }
      const includedUsers =
        plan?.costs['multiple-user-login']?.included_units || 1;
      return (
        <>
          <b>{formatNumber(includedUsers)}</b>{' '}
          {includedUsers === 1 ? 'user included' : 'active monthly users'}
        </>
      );
    },
  },
  'included-pexp': {
    key: 'included-pexp',
    Icon: ValuationReportsIncludedIcon,
    Label: ({ plan }) => {
      const includedPexp =
        plan?.costs['pexp-dynamic-report']?.included_units || 0;
      return (
        <>
          <b>{formatNumber(includedPexp)}</b> custom valuation reports included
          per month at no extra cost
        </>
      );
    },
  },
  'included-agile-insights': {
    key: 'included-agile-insights',
    // TODO: Icon
    Icon: AvmReportsIncludedIcon,
    Label: ({ plan }) => {
      const includedAgileInsights = plan?.costs['om-vr']?.included_units || 0;
      return (
        <>
          <b>{formatNumber(includedAgileInsights)}</b> AVM PDF reports included
          per month at no extra cost
        </>
      );
    },
  },
  'included-portfolio': {
    key: 'included-portfolio',
    // TODO: Icon
    Icon: PropertiesIcon,
    Label: ({ plan }) => {
      const includedPortfolio =
        plan?.costs['portfolio-monitor-product-access']?.included_units || 0;
      return (
        <>
          Monitoring for <b>{formatNumber(includedPortfolio)}</b> properties
          included at no extra cost
        </>
      );
    },
  },
  canaryai: {
    key: 'canaryai',
    Icon: APP_CONFIG_CANARYAI.Icon,
    Label: 'Full access to CanaryAI',
  },
  'in-app-support': {
    key: 'in-app-support',
    Icon: CustomerSupportIcon,
    Label: 'Robust knowledge base and in-app customer support',
  },
  'brokerage-services': {
    key: 'brokerage-services',
    Icon: NationwideBrokerageIcon,
    Label: 'Nationwide real estate brokerage services included',
  },
  'custom-pricing': {
    key: 'custom-pricing',
    Icon: ValuationReportsIncludedIcon,
    Label: (
      <>
        <b>Custom</b> pricing and product inclusion with negotiable minimums
      </>
    ),
  },
  'all-products-access': {
    key: 'all-products-access',
    Icon: ProductGridIcon,
    Label: 'Access to all products within the HouseCanary platform',
  },
  'most-products-access': {
    key: 'most-products-access',
    Icon: ProductGridIcon,
    Label: 'Access to most products within the HouseCanary platform',
  },
  'core-api-access': {
    key: 'core-api-access',
    Icon: CoreApiIcon,
    Label: ({ onClick }) => {
      if (onClick) {
        return (
          <>
            Access to HouseCanary's core API for scalable data access with
            additional{' '}
            <Anchor dataHcName="usage-based-costs-link" onClick={onClick}>
              usage-based costs
            </Anchor>
          </>
        );
      }
      return "Access to HouseCanary's core API for scalable data access with additional usage-based costs";
    },
  },
  'advanced-feature-access': {
    key: 'advanced-feature-access',
    Icon: AdvancedApiIcon,
    Label: 'Access to advanced APIs and platform features',
  },
  'custom-products': {
    key: 'custom-products',
    Icon: ScalableDatasetIcon,
    Label: 'Access to additional custom products and scalable datasets',
  },
  'solutions-engineering': {
    key: 'solutions-engineering',
    Icon: SolutionsEngineerIcon,
    Label: 'Exposure to Solutions Engineering and Research teams',
  },
  sso: {
    key: 'sso',
    Icon: SsoIcon,
    Label: 'Configurable SSO',
  },
  soc2: {
    key: 'soc2',
    Icon: Soc2Icon,
    Label: 'Access to SOC2 reports by request',
  },
  'custom-compliance': {
    key: 'custom-compliance',
    Icon: CustomQuestionnairesIcon,
    Label: 'Custom compliance questionnaires',
  },
};
type SelfServePlanFeatureKey = keyof typeof SELF_SERVE_PLAN_FEATURES;
export const SELF_SERVE_PLAN_FEATURES_BY_TIER: Record<
  SelfServicePlanTier,
  SelfServePlanFeatureKey[]
> = {
  Basic: [
    'included-users',
    'included-pexp',
    'canaryai',
    'in-app-support',
    'brokerage-services',
    'most-products-access',
  ],
  Pro: [
    'included-users',
    'included-pexp',
    'included-agile-insights',
    'included-portfolio',
    'canaryai',
    'in-app-support',
    'brokerage-services',
    'all-products-access',
    'core-api-access',
  ],
  Teams: [
    'included-users',
    'included-pexp',
    'included-agile-insights',
    'included-portfolio',
    'canaryai',
    'in-app-support',
    'brokerage-services',
    'all-products-access',
    'core-api-access',
    'advanced-feature-access',
    'sso',
    'soc2',
  ],
  Enterprise: [
    'included-users',
    'custom-pricing',
    'canaryai',
    'in-app-support',
    'brokerage-services',
    'all-products-access',
    'core-api-access',
    'advanced-feature-access',
    'custom-products',
    'solutions-engineering',
    'sso',
    'soc2',
    'custom-compliance',
  ],
};
